import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2, HeartHandshake, Landmark, ChevronRight, Search,
  Stethoscope, FlaskConical, FileText, Network, Users, SlidersHorizontal, KanbanSquare,
  BookOpen, MessageSquare, Settings as SettingsIcon, type LucideIcon,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'

/**
 * /settings — hub de configurações organizado em MÓDULOS.
 *
 * Cada módulo agrupa itens relacionados (catálogos, integrações, etc.).
 * No topo há uma barra de busca que filtra itens por label/descrição
 * dentro de todos os módulos em tempo real.
 */
interface SettingItem {
  to: string
  label: string
  icon: LucideIcon
  description: string
}
interface SettingModule {
  key: string
  title: string
  description: string
  items: SettingItem[]
}

const MODULES: SettingModule[] = [
  {
    key: 'system',
    title: 'Sistema',
    description: 'Parâmetros globais e preferências do sistema.',
    items: [
      { to: '/settings/parametros', label: 'Parâmetros', icon: SlidersHorizontal, description: 'Valores globais usados pelo sistema (ex.: valor do contrato principal do credenciamento)' },
    ],
  },
  {
    key: 'workflow',
    title: 'Workflow',
    description: 'Catálogos auxiliares e tabelas de referência usadas em todo o sistema.',
    items: [
      { to: '/settings/workflow/banks',                label: 'Bancos',              icon: Landmark,       description: 'Lista FEBRABAN' },
      { to: '/settings/workflow/marital-statuses',     label: 'Estados civis',       icon: HeartHandshake, description: '7 entradas oficiais' },
      { to: '/settings/workflow/degree-relationships', label: 'Graus de parentesco', icon: Building2,      description: 'Catálogo ANS' },
      { to: '/settings/workflow/specialties',          label: 'Especialidades',      icon: Stethoscope,    description: 'Especialidades médicas (TUSS)' },
      { to: '/settings/workflow/tests',                label: 'Exames',              icon: FlaskConical,   description: 'Exames laboratoriais e de imagem' },
    ],
  },
  {
    key: 'support',
    title: 'Atendimento',
    description: 'Canais da Central de Atendimento e integrações de mensageria.',
    items: [
      { to: '/settings/support/whatsapp', label: 'WhatsApp (Meta Business)', icon: MessageSquare, description: 'Credenciais da integração, webhook e vínculo do número com a Central de Atendimento' },
      { to: '/support/settings',          label: 'Equipes, filas e respostas', icon: SettingsIcon, description: 'Filas de atendimento, etiquetas, respostas rápidas, canais e indicadores' },
    ],
  },
  {
    key: 'integrations',
    title: 'Integrações',
    description: 'Credenciais e parâmetros das integrações com sistemas externos.',
    items: [
      { to: '/settings/integrations/medicar', label: 'Medicar (TOTVS PLS)', icon: Stethoscope, description: 'Usuário, senha, tenant e dados de contrato da API de beneficiários' },
    ],
  },
  {
    key: 'credentialing',
    title: 'Credenciamento',
    description: 'Configurações operacionais do processo de credenciamento.',
    items: [
      { to: '/settings/credentialing/types',                 label: 'Tipos de credenciado',    icon: Network,      description: 'Categorias de credenciados (clínica, médico, laboratório, etc.)' },
      { to: '/settings/credentialing/steps',                 label: 'Etapas',                  icon: FileText,     description: 'Etapas do kanban de credenciamento' },
      { to: '/settings/credentialing/commission-structures', label: 'Comissionamento',        icon: Network,      description: 'Tabelas de comissão por contratos fechados' },
    ],
  },
  {
    key: 'commercial',
    title: 'Comercial',
    description: 'Configurações operacionais do processo comercial.',
    items: [
      { to: '/settings/commercial/commission-structures', label: 'Comissionamento',    icon: Network, description: 'Tabelas de comissão por parcela e tipo de usuário' },
      { to: '/settings/commercial/modalities',            label: 'Modalidades',         icon: Users,   description: 'Tipos de plano (Individual, Familiar, ) com idade mínima e graus aceitos' },
      { to: '/settings/commercial/products',              label: 'Produtos',            icon: Network, description: 'Bundle de serviços × prestadores + regras de elegibilidade por modalidade' },
      { to: '/settings/commercial/services',              label: 'Serviços',            icon: FileText, description: 'Catálogo de serviços e os prestadores que os executam' },
      { to: '/settings/commercial/manual-blocks',         label: 'Manual de utilização', icon: BookOpen, description: 'Blocos de conteúdo do manual, vinculados às coberturas da proposta' },
      { to: '/settings/commercial/service-providers',     label: 'Prestadores',         icon: Users,    description: 'Quem executa os serviços (Rede Própria, Medicar, Porto Seguro, MAG, PAP)' },
      { to: '/settings/commercial/report-templates',      label: 'Modelos de relatório', icon: FileText, description: 'Layouts de exportação por prestador — colunas, formato (Excel/CSV) e regra de família' },
      { to: '/settings/commercial/proposal-catalog',      label: 'Catálogo de propostas', icon: SlidersHorizontal, description: 'Linhas, modalidades e coberturas da proposta, tabelas de preço (importar planilha) e cadastros' },
      { to: '/settings/commercial/proposal-templates',    label: 'Modelos de proposta',  icon: FileText, description: 'PDFs fixos (capa, corpo, contracapa) e observações padrão das propostas comerciais' },
      { to: '/settings/commercial/proposal-stages',        label: 'Etapas (propostas)',   icon: KanbanSquare, description: 'Etapas do kanban de propostas comerciais (ordem do funil)' },
    ],
  },
]

// Ordena módulos por título e itens por label (pt-BR), sem mutar o array original.
// Garantia da regra global: a sequência sempre respeita a ordem alfabética,
// mesmo que novos módulos/itens sejam adicionados fora de ordem em MODULES.
const SORTED_MODULES: SettingModule[] = [...MODULES]
  .map((m) => ({ ...m, items: [...m.items].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')) }))
  .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'))

export function SettingsIndexPage() {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  const modulesFiltered = useMemo(() => {
    if (!q) return SORTED_MODULES
    return SORTED_MODULES.map((m) => ({
      ...m,
      items: m.items.filter((it) =>
        it.label.toLowerCase().includes(q) ||
        it.description.toLowerCase().includes(q),
      ),
    })).filter((m) => m.items.length > 0)
  }, [q])

  return (
    <>
      <PageHeader
        title="Configurações"
        subtitle="Catálogos, integrações e preferências do sistema"
        crumbs={[{ label: 'Configurações' }]}
      />

      <div className="mb-6 max-w-md">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar configuração"
          iconLeft={<Search size={14} />}
        />
      </div>

      {modulesFiltered.length === 0 ? (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-10 text-center text-sm text-gray-500">
          Nenhuma configuração encontrada para <strong className="text-gray-700">"{query}"</strong>.
        </div>
      ) : (
        <div className="space-y-8">
          {modulesFiltered.map((mod) => (
            <ModuleSection key={mod.key} mod={mod} highlight={q} />
          ))}
        </div>
      )}
    </>
  )
}

function ModuleSection({ mod, highlight }: { mod: SettingModule; highlight: string }) {
  return (
    <section>
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">{mod.title}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{mod.description}</p>
      </header>
      <div className="bg-white rounded-md ring-1 ring-gray-200 divide-y divide-gray-100">
        {mod.items.map((it) => {
          const Icon = it.icon
          return (
            <Link
              key={it.to}
              to={it.to}
              className={cn(
                'group flex items-center gap-4 px-4 sm:px-5 py-4 hover:bg-gray-50 transition cursor-pointer',
              )}
            >
              <div className="w-10 h-10 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center ring-1 ring-brand-100 shrink-0">
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">
                  {highlightText(it.label, highlight)}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {highlightText(it.description, highlight)}
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-300 group-hover:text-brand-500 shrink-0" />
            </Link>
          )
        })}
      </div>
    </section>
  )
}

/** Marca o termo buscado em <mark>. Case-insensitive, primeira ocorrência. */
function highlightText(text: string, query: string) {
  if (!query) return text
  const idx = text.toLowerCase().indexOf(query)
  if (idx < 0) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-100 text-amber-900 rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}
